import { Response, ValidatorsProps, apiAbortController } from '@ui/src';
import api from '../../../../api/api';
import {
  NewCofProps,
  CofStatus,
  SignatureType,
  CustomerProps,
  Balance,
  UserAccountProps,
} from '../../../common/types';
import { CofProps, CorporateOverviewGeneral } from '../../types';
import { NatureOfControlEnum } from '../../../new-application/types';

export interface CreateCorporateCustomerValidatorResponse {
  data: {
    createClientOfficialSchema: ValidatorsProps;
    updateClientOfficialSchema: ValidatorsProps;
  };
}

export const corporateCustomersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCorporateUsers: build.query<Response<CustomerProps>, void>({
      query: (query) => ({
        url: `/v1/clients-organizations${query}&type=CORPORATE`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualUsersList: build.query<Response<CustomerProps>, string>({
      query: (query) => ({
        url: `/v1/clients-organizations${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCreateCorporateCustomerValidator: build.query<
      CreateCorporateCustomerValidatorResponse,
      void
    >({
      query: () => ({
        url: '/v1/clients-officials/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateUser: build.query<{ data: CustomerProps }, string>({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateCOF: build.query<Response<CofProps>, string>({
      query: (id) => ({
        url: `/v1/clients-officials?organizationId=${id}&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateCOF: build.mutation<
      {
        data: CofProps;
      },
      {
        id: string;
        body: {
          position: string;
          percentage?: string;
          natureOfControl?: NatureOfControlEnum;
          companyOfficialType: string;
          signatureType: SignatureType;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-officials/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addCorporateCOF: build.mutation<
      {
        data: CofProps;
      },
      NewCofProps
    >({
      query: (body) => ({
        url: '/v1/clients-officials',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCofStatus: build.mutation<
      {
        data: CofProps;
      },
      {
        id: string;
        body: {
          status: CofStatus;
          rejectionReason?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-officials/${data.id}/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateAccountBalance: build.query<Response<Balance>, string>({
      query: (id) => ({
        url: `/v1/accounts/${id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateInfo: build.mutation<
      {
        data: CustomerProps;
      },
      { body: Partial<CorporateOverviewGeneral>; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCorporateAccount: build.mutation<
      {
        data: UserAccountProps;
      },
      {
        organizationId: string;
        body: { description: string };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.organizationId}/account`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),

    getCorporateMainChannel: build.query<Response<{ id: string }>, string>({
      query: (id) => ({
        url: `/v1/channels?organizationId=${id}&channelType=MAIN`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetCorporateUsersQuery,
  useLazyGetCorporateUsersQuery,
  useGetCreateCorporateCustomerValidatorQuery,
  useLazyGetCorporateUserQuery,
  useLazyGetCorporateAccountBalanceQuery,
  useCreateCorporateAccountMutation,
  useUpdateCorporateInfoMutation,
  useLazyGetCorporateCOFQuery,
  useUpdateCorporateCOFMutation,
  useAddCorporateCOFMutation,
  useLazyGetIndividualUsersListQuery,
  useUpdateCofStatusMutation,
  useLazyGetCorporateMainChannelQuery,
} = corporateCustomersApi;

export const {
  endpoints: {
    getCorporateUsers,
    getCreateCorporateCustomerValidator,
    getCorporateUser,
    getCorporateAccountBalance,
    createCorporateAccount,
    updateCorporateInfo,
    getCorporateCOF,
    updateCorporateCOF,
    addCorporateCOF,
    getIndividualUsersList,
    updateCofStatus,
    getCorporateMainChannel,
  },
} = corporateCustomersApi;
